import { defineStore } from 'pinia'
import { AnnStore } from '@/front/stores/ann_store.js';
import { ClickStore } from '@/front/stores/click_store.js';
import { LeadCaptureStore } from '@/front/stores/lead_capture_store.js';
import { PaymentStore } from '@/front/stores/payment_store.js';
import { VideoService } from '@/front/services/VideoService.js';
import { PlaybackStore } from '@/front/stores/playback_store.js';
import { MessageComposer } from '@/front/composables/MessageComposer.js';
import { VideoStore } from '@/front/stores/video_store.js';
import { preloadManager } from '@/front/services/PreloadManager.js';

export const PlayerStore = defineStore('player', {
  state: () => {
    return {
      activeSrc: null,
      activeTracks: [],
      autoplayUnmuted: true,
      available: true, // Trigger to re-initialize player if needed
      branchCount: 0, // Number of times the video has been branched
      branching: false,
      branchTransition: null,
      currentScreenshot: null,
      clipEndTime: 0,
      decitime: 0.0,
      fulltime: 0, // Rounded
      hitPoints: [], // Debugging tool to track deci seconds we've passed
      editReturnTime: 0.0, // Stores where we were in editor for tab swithing
      editReturnToken: null, // Stores the token of the video we were in editor for tab swithing
      initialized: false,
      isDebug: window.location.search.includes("gostamp") || window.location.search.includes("doot"),
      isDebugPerformance: window.location.search.includes("goperf=1"),
      isDev: window.VITE_DEVELOPMENT == 1,
      isMobile: window.innerWidth < 600,
      isFullscreen: false,
      layerDimensions: {
        top: 0,
        left: 0,
        height: 100,
        width: 100,
      },
      layerStyle: {
        top: 0,
        left: 0,
        height: '100%',
        width: '100%',
      },
      fixedWrapperStyle: {
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      },
      player: null,
      refPlayer: null,
      playerState: {},
      ready: false,
      screenToVideoRatio: 1.0,
      started: false, // Current video has been started,
      startTime: 0, // start time of the playback
      videoDimensions: {
        width: 0,
        height: 0,
      },
      videoElement: null, // Holds actual <video> tag
      mediaPlayer: null, // Holds the media player div wrapper
      loweredVolume: false,
      originalVolume: 1.0,
      lowerVolumeInterval: null,
      hasTriggeredEnd: false, // Track if we've already triggered end to prevent multiple calls
      lastCurrentTime: null, // Track previous time to detect loop restarts
      currentVideoSource: null, // Track current video source for branching detection
      speed: 1.0,
    }
  },

  actions: {

    resetVideoElement() {
      // Force re-attachment of video element (useful after source changes)
      this.videoElement = null;
      this.attachVideoElement();
    },
    
    async lowerVolume() {
      const videoStore = VideoStore();
      if (this.loweredVolume || !videoStore.video.design.fade_audio || !this.videoElement) return;
      
      this.originalVolume = this.videoElement.volume;
      this.loweredVolume = true;
      
      const fadeStartTime = performance.now();
      const fadeDuration = 800; // 8 steps * 100ms
      
      const fade = () => {
        const elapsed = performance.now() - fadeStartTime;
        const progress = Math.min(elapsed / fadeDuration, 1);
        
        this.videoElement.volume = this.originalVolume * (1 - progress);
        
        if (progress < 1) {
          requestAnimationFrame(fade);
        }
      };
      
      requestAnimationFrame(fade);
    },
    setVolume(volume){
      log.info(2,"💟 PlayerStore setVolume", volume)
      if (this.videoElement) {
        this.videoElement.volume = volume
      }
    },
    async resetVolume(){
      if (!this.loweredVolume || !this.videoElement) {
        return
      }
      log.info(1,"💟 PlayerStore resetVolume", this.originalVolume)
      this.videoElement.volume = this.originalVolume
      if (!this.lowerVolumeInterval) {
        return
      }
      clearInterval(this.lowerVolumeInterval)
      this.lowerVolumeInterval = null
      this.loweredVolume = false
    },
    async takeScreenshot() {
      return VideoService.takeVideoScreenshot(this.videoElement).then((dataUrl) => {
        // log.info(1,"🌜 PlayerStore takeScreenshot", dataUrl)
        this.currentScreenshot = dataUrl
        return dataUrl
      })
    },
    async handleTimeUpdate(time) {
      const clickStore = ClickStore();
      const videoStore = VideoStore();
      // Changed this to manager only to keep thumbnail active when using t= timestamp and autoplay=0 to not autoplay
      if (time != 0 && videoStore.video.manage) {
        this.started = true
      }
      // iOS Safari workaround: ignore spurious time=0 after playback has started
      if (this.started && time < 0.1 && this.decitime < 0.2) {
        log.info(1,"💟 PlayerStore handleTimeUpdate iOS spurious zero, ignoring")
        return
      }

      if (clickStore.hasActiveDialog) {
        log.info(1,"💟 PlayerStore handleTimeUpdate hasActiveDialog, skipping")
        this.pause("playerStore handleTimeUpdate hasActiveDialog")
        return
      }
      const annStore = AnnStore();

      // log.info(1,"PlayerStore handleTimeUpdate ", time)
      const currentDecitime = Math.round(time * 10) / 10;

      // Check if the decitime has changed since the last update
      if (currentDecitime !== this.decitime) {
        const newFulltime = Math.floor(time)
        if (newFulltime !== this.fulltime) {
          const { emitMessage } = MessageComposer();
          this.fulltime = newFulltime
          if (newFulltime % 5 == 0 && this.isDebugPerformance) {
            if (window.printGlobalRenderReport) {
              window.printGlobalRenderReport();
            }
            if (window.piniaMonitor) {
              window.piniaMonitor.printReport()
            }
            if (window.mainThreadMonitor) {
              window.mainThreadMonitor.printReport()
            }
            if (window.stateProfiler) {
              window.stateProfiler.printReport()
            }
          }
          emitMessage("time_update", this.fulltime)
          this.updateRatios()
        }
        // Decitime has changed, process the interaction map
        // Calcualte if we seeked elsewhere, reset if so, otherwise normal playback is 0.1 more
        let diff = currentDecitime * 10 - this.decitime * 10
        // log.info(1,"🕒 " + this.decitime, "currentDecitime", currentDecitime, "diff", diff)
        let needReset = (diff !== 1)
        annStore.clearDoneAt(this.decitime)
        this.decitime = currentDecitime;
        if (this.isDebug) {
          this.hitPoints.push(this.decitime)
        }
        log.info(1, "🕒 " + this.decitime)
        annStore.updateActive(currentDecitime);
        if (annStore.isEditing) {
          log.info(1,"💟 PlayerStore handleTimeUpdate isEditing, pausing")
          this.pause("playerStore handleTimeUpdate isEditing")
          return
        }

        // Update the last decitime to the current one
        // log.info(1,"\n🕔", this.decitime)
      }
    },
    attachVideoElement() {
      this.videoElement = document.querySelector('#media-provider video')
      this.mediaPlayer = document.querySelector('#media-provider')
      if (this.videoElement) {
        // Disable picture-in-picture functionality
        this.videoElement.setAttribute('disablePictureInPicture', 'true');
        this.videoElement.disablePictureInPicture = true;

        // Also disable the context menu to prevent right-click PiP option
        this.videoElement.addEventListener('contextmenu', (e) => {
          e.preventDefault();
        });
      }
      // log.info(1,"🌜 PlayerStore attachVideoElement videoElement", this.videoElement)
    },
    setPlayerState(state) {
      let wasSeeking = this.playerState.seeking
      let shouldUpdateRatios = this.playerState.width != state.width || this.playerState.height != state.height || state.width == 0 || state.height == 0 || this.playerState.paused
      this.playerState = state
      this.speed = state.playbackRate
      this.attachVideoElement()
      if (shouldUpdateRatios) {
        this.updateRatios()
      }
      if (wasSeeking && !this.playerState.seeking) {
        const annStore = AnnStore();
        log.info(1,"Finished Seeking, reset active to " + this.decitime)
        annStore.resetActive(this.decitime, "setPlayerState")
      }
    },
    toggleMute(muted) {
      this.videoElement ? this.videoElement.muted = muted : log.info(1,"💦 PlayerStore toggleMute no videoElement")
    },
    reload() {
      log.info(1,"🌜 PlayerStore reload")
      this.videoElement?.load()
    },
    resetEndedState() {
      if (this.playerState.ended) {
        log.info(1,"💟 PlayerStore ResetEndedState - video ended, restoring seekability")
        
        // Reset the video element to a seekable state
        if (this.videoElement) {
          // For ended videos, we need to reset currentTime before seeking works
          this.videoElement.currentTime = Math.max(0, this.safeDuration - 0.1);
        }
        
        // Also try the player API
        this.player.play()
        setTimeout(() => {
          this.player.pause()
        }, 100) // Reduced timeout for faster recovery
      }
    },
    
    // Reset end detection state for new videos or when user seeks away from end
    resetEndDetection(reason = 'unknown') {
      log.info(1,`💟 PlayerStore resetEndDetection - ${reason}`);
      this.hasTriggeredEnd = false;
      this.lastCurrentTime = null;
    },
    
    // Check if video source has changed (for branching)
    checkVideoSourceChange(newSource) {
      if (newSource && this.currentVideoSource !== newSource) {
        log.info(1,`💟 PlayerStore video source changed: ${this.currentVideoSource} → ${newSource}`);
        this.currentVideoSource = newSource;
        this.resetEndDetection('source change');
        return true;
      }
      return false;
    },
    rewind() {
      const videoStore = VideoStore();
      const annStore = AnnStore();
      let amount = (videoStore.video.skip_amount || 5)
      log.info(1,"🌜 PlayerStore rewind", amount)
      annStore.markIntentionalSeek(); // Mark as intentional seek
      this.seek(this.boundTime(this.decitime - amount))
    },
    fastForward() {
      const videoStore = VideoStore();
      const annStore = AnnStore();
      let amount = (videoStore.video.skip_amount || 5)
      annStore.markIntentionalSeek(); // Mark as intentional seek
      log.info(1,"🌜 PlayerStore fastForward", amount)
      this.seek(this.boundTime(this.decitime + amount))
    },
    boundTime(time) {
      return Math.max(0, Math.min(time, this.playerState.duration - 0.1))
    },
    async seekAndPlay(time, timeout = 30000) {
      log.info(1,"🌜 PlayerStore seekAndPlay", time)
      const annStore = AnnStore();
      annStore.markIntentionalSeek(); // Mark as intentional seek
      annStore.lastUpdateTime = null
      await this.resetEndedState()

      try {
        await new Promise((resolve, reject) => {
          this.attachVideoElement();

          // 🔁 Fix: Reset ended state
          // if (this.videoElement?.ended) {
          //   log.info(1,"🔁 Video was ended, rewinding off end to reenable seek");
          //   this.player.currentTime = 5
          //   return
          // }

          const seekedHandler = () => {
            this.videoElement?.removeEventListener('seeked', seekedHandler);
            log.info(1,"✅ PlayerStore seekAndPlay - seek completed");
            // Clear intentional seek flag after seek completes
            setTimeout(() => annStore.clearIntentionalSeek(), 100);
            resolve();
          };

          this.videoElement?.addEventListener('seeked', seekedHandler);

          let t = this.boundTime(time)
          if (this.videoElement) {
            this.videoElement.currentTime = t;
          } else {
            log.info(1,"💦 PlayerStore seekAndPlay no videoElement", this.player);
            if (this.player) {
              this.player.currentTime = t;
            } else {
              log.info(1,"💦 PlayerStore seekAndPlay no player", this.player)
            }
          }

          log.info(1,"💟 PlayerStore seekAndPlay resetting active", time);
          annStore.resetActive(time, "seekAndPlay");
          this.tryClearManagerClicks()

          setTimeout(() => {
            this.videoElement?.removeEventListener('seeked', seekedHandler);
            reject(new Error('Seek timeout after ' + timeout + 'ms'));
          }, timeout);
        });

        log.info(1,"🎬 PlayerStore seekAndPlay - starting playback at " + time);
        await this.play("seekAndPlay");

      } catch (error) {
        log.info(1,"🔴 PlayerStore seekAndPlay error:", error);
        throw error;
      }
    },

    // Modify your existing seek method to be async for consistency
    async seek(time) {
      log.info(1,"🌜 PlayerStore seek", time)
      
      // Reset end detection if seeking away from end (allows re-triggering end)
      if (this.safeDuration && time < this.safeDuration - 2.0) {
        this.resetEndDetection('seek away from end');
      }
      
      const annStore = AnnStore();
      annStore.markIntentionalSeek(); // Mark as intentional seek
      annStore.lastUpdateTime = null
      await this.resetEndedState()
      const clickStore = ClickStore()
      try {
        await new Promise((resolve, reject) => {
          this.attachVideoElement()
          // if (!this.videoElement) {
          //   reject(new Error('No video element found'))
          //   return
          // }

          const seekedHandler = () => {
            this.videoElement?.removeEventListener('seeked', seekedHandler)
            // Clear intentional seek flag after seek completes
            setTimeout(() => annStore.clearIntentionalSeek(), 100);
            resolve()
          }

          this.videoElement?.addEventListener('seeked', seekedHandler)
          let t = this.boundTime(time)
          if (this.videoElement) {
            this.videoElement.currentTime = t
          } else {
            log.info(1,"💦 PlayerStore seek no videoElement", this.player)
            if (this.player) {
              this.player.currentTime = t
            } else {
              log.info(1,"💦 PlayerStore seek no player", this.player)
            }
          }

          // setTimeout(() => {
          //   this.videoElement?.removeEventListener('seeked', seekedHandler)
          //   reject(new Error('Seek timeout'))
          // }, 5000)
        })
        annStore.clearDoneAt(time)

        if (!clickStore.questionActive) {
          log.info(1,"💟 PlayerStore seek resetting active", time)
          annStore.resetActive(time, "seek")
        } else {
          log.info(1,"💟 PlayerStore seek questionActive, skipping resetActive")
        }
        this.tryClearManagerClicks()
        if (!annStore.isEditing) {
          log.info(1,"💟 PlayerStore seek update active", time)
          annStore.updateActive(time)
        } else {
          log.info(1,"💟 PlayerStore seek isEditing, skipping updateActive")
        }
      } catch (error) {
        log.info(1,"🔴 PlayerStore seek error:", error)
        throw error
      }
    },
    togglePlayer(from = "none") {
      const annStore = AnnStore();
      const clickStore = ClickStore()
      const playbackStore = PlaybackStore()
      this.started = true
      if (clickStore.hasActiveDialog) {
        log.info(1,"💟 PlayerStore togglePlayer hasActiveDialog, skipping togglePlayer")
        return
      }


      log.info(1,"PlayerStore togglePlayer (" + from + ")")
      playbackStore.beginTracking()

      // Check if pausing until interaction click
      if (annStore.pausedInteraction) {
        log.info(1,"💟 PlayerStore togglePlayer pausedInteraction", annStore.pausedInteraction, annStore.pausedInteraction.exact_time == this.decitime)
        if (annStore.pausedInteraction.exact_time == this.decitime) {
          log.info(1,annStore.pausedInteraction.exact_time, this.decitime)
          log.info(1,"💟 PlayerStore togglePlayer pausedInteraction returning", annStore.pausedInteraction)
          return
        } else {
          log.info(1,"💟 PlayerStore togglePlayer pausedInteraction not at time, resetting")
          annStore.pausedInteraction = null
        }
      }

      // Check if any must-click interactions on screen
      if (annStore.mustClickInteractions.length > 0) {
        let block = false
        annStore.mustClickInteractions.forEach(ann => {
          if (ann.exact_finish == this.decitime) {
            block = true
          }
        })
        if (block) {
          log.info(1,"💟 PlayerStore togglePlayer mustClickInteractions returning", annStore.mustClickInteractions)
          return
        } else {
          log.info(1,"💟 PlayerStore togglePlayer mustClickInteractions not at time, resetting")
          annStore.mustClickInteractions = []
        }
      } else {
        this.tryClearManagerClicks()
      }

      // Clear pause for timed pause
      clickStore.finishTimedPause()

      if (!this.autoplayUnmuted && (this.playerState.muted || this.playerState.volume == 0)) {
        log.info(1,"🌜 PlayerStore togglePlayer autoplayUnmuted", this.playerState.muted, this.playerState.volume)
        this.toggleMute(false)
        this.autoplayUnmuted = true
        return
      }
      if (this.playerState.seeking) {
        return
      }
      if (annStore.isEditing) {
        this.pause("playerStore togglePlayer isEditing")
        return
      }
      if (this.playerState.paused) {
        this.playerState.paused = false
        this.play("playerStore togglePlayer")
      } else {
        this.playerState.paused = true
        this.pause("playerStore togglePlayer")
      }
    },

    playIfStarted() {
      if (this.started) {
        this.play("playerStore playIfStarted")
      } else {
        log.info(1,"💟 PlayerStore playIfStarted not started")
      }
    },
    tryClearManagerClicks() {
      const playbackStore = PlaybackStore();
      const annStore = AnnStore();
      if (playbackStore.manager) {
        // log.info(1,"💟 PlayerStore clearManagerClicks")
        annStore.anns.forEach(ann => {
          if (ann.must_click) {
            ann.clicked = false
          }
        })
      }
    },
    play(from) {
      log.info(1,"🌜 🌜 🌜 🌜 🌜 🌜 🌜 🌜 PlayerStore play", from)
      if (!this.ready) {
        log.info(1,"💦 PlayerStore play not ready")
        return
      }
      // check if the tab is in focus
      // log.info(1,"💟 PlayerStore play document.hasFocus()", document.hasFocus())
      if (this.started && !document.hasFocus()) {
        log.info(1,"💟 PlayerStore play tab is not in focus, skipping play")
        return
      }
      
      const clickStore = ClickStore();

      if (clickStore.hasActiveDialog) {
        log.info(1,"💟 PlayerStore play hasActiveDialog returning", clickStore.hasActiveDialog)
        return
      }
      const annStore = AnnStore();

      if (annStore.mustClickInteractions.length > 0) {
        log.info(1,"💟 PlayerStore play mustClickInteractions returning", annStore.mustClickInteractions)
        return
      }
      const leadCaptureStore = LeadCaptureStore();
      const paymentStore = PaymentStore();
      this.attachVideoElement()
      // Check lead capture triggers
      if (leadCaptureStore.checkTriggers(this.decitime)) {
        log.info(1,"💟 PlayerStore play leadCaptureStore checkTriggers returning", leadCaptureStore.checkTriggers(this.decitime))
        return
      } else if (paymentStore.checkTriggers(this.decitime)) {
        log.info(1,"💟 PlayerStore play paymentStore checkTriggers returning", paymentStore.checkTriggers(this.decitime))
        return
      } else if (clickStore.hasActiveDialog) {
        // Showing dialog that means we should not play
        log.info(1,"💟 PlayerStore play hasActiveDialog returning", clickStore.hasActiveDialog)
        return
      }

      // Check if any must-click interactions on screen
      var good = true
      annStore.mustClickInteractions.forEach(ann => {
        if (!ann.clicked && ann.exact_finish + 0.1 == this.decitime) {
          good = false
        }
      })

      if (!good) {
        log.info(1,"💟 PlayerStore play mustClickInteractions returning", annStore.mustClickInteractions)
        return
      }
      annStore.resetActive(this.decitime, "play")
      annStore.updateActive(this.decitime)

      // Initialize preloading when video starts playing for the first time
      if (!this.started) {
        setTimeout(() => {
          log.info(3, "🎬 Initializing preload manager after video start")
          preloadManager.init(annStore.anns, this.decitime)
        }, 2000) // Wait 2 seconds after video starts
      }

      if (this.videoElement) {
        this.videoElement.play()
      } else {
        log.info(1,"💦 PlayerStore play no videoElement", this.player)
        this.player.play()
      }
    },
    pause(from) {
      log.info(1,"🌜 🌜 🌜 🌜 🌜 🌜 🌜 🌜 PlayerStore pause", from)
      this.attachVideoElement()
      if (this.videoElement) {
        this.videoElement.pause()
      } else {
        log.info(1,"💦 PlayerStore pause no videoElement", this.player)
        this.player.pause()
      }
    },
    clipFinished() {
      log.info(1,"🌜 PlayerStore clipFinished")
      this.play("playerStoreclipFinished")
    },
    updateRatios() {
      if (!this.ready || !this.videoElement) {
        let el = document.getElementById('player-wrapper')
        if (!el) {
          log.info(3,"💦 PlayerStore updateRatios no player-wrapper")
          return
        }
        let rect = el.getBoundingClientRect()
        log.info(3,"💦 PlayerStore updateRatios player-wrapper", rect)
        this.layerDimensions = {
          top: 0,
          left: 0,
          height: rect.height,
          width: rect.width,
        }
        this.videoDimensions = {
          width: rect.width,
          height: rect.height,
        }
        
        this.fixedWrapperStyle = {
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        }
        return
      }
      // log.info(1,"🌜 PlayerStore updateRatios", this.playerState.width, this.playerState.height)
      // Container
      const containerWidth = this.playerState.width
      const containerHeight = this.playerState.height
      const containerRatio = containerWidth / containerHeight
      // Video
      const videoWidth = this.videoElement?.videoWidth || containerWidth
      const videoHeight = this.videoElement?.videoHeight || containerHeight
      const videoRatio = videoWidth / videoHeight

      // Ratio between container and video
      this.screenToVideoRatio = (containerRatio / videoRatio)
      let width = 100
      let height = 100
      let left = 0
      let top = 0

      let rect = this.videoElement.getBoundingClientRect()
      this.fixedWrapperStyle = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      }

      // If cover mode, use full area
      const videoStore = VideoStore();
      const coverMode = videoStore.video.design?.cover_mode && !videoStore.video.manage
      if (coverMode) {
        // log.info(1,"💟 PlayerStore updateRatios coverMode", coverMode)
        this.layerStyle = {
          width: '100%',
          height: '100%',
          left: 0,
          top: 0,
        }
        this.fixedWrapperStyle = this.layerStyle
        this.layerDimensions = {
          top: 0,
          left: 0,
          height: containerHeight,
          width: containerWidth,
        }

        this.videoDimensions = {
          width: videoWidth,
          height: videoHeight,
        }
        return
      }

      this.isMobile = containerWidth < 600

      if (containerRatio > videoRatio) {
        width = containerHeight * videoRatio
        height = containerHeight
      } else {
        width = containerWidth
        height = containerWidth / videoRatio
      }
      left = (containerWidth - width) / 2
      top = Math.max(0, (containerHeight - height) / 2)
      if (isNaN(height)) {
        // height = window.innerHeight
        height = 0
      }

      // Round values to 1 decimal place
      left = Number(left.toFixed(1))
      top = Number(top.toFixed(1))
      width = Number(width.toFixed(1))
      height = Number(height.toFixed(1))

      // Update video dimensions
      this.videoDimensions = {
        width: videoWidth,
        height: videoHeight,
      }

      // Check if dimensions have changed before updating
      const dimensionsChanged =
        this.layerDimensions?.top !== top ||
        this.layerDimensions?.left !== left ||
        this.layerDimensions?.height !== height ||
        this.layerDimensions?.width !== width;

      if (dimensionsChanged || !this.layerDimensions) {
        // log.info(1,"💟 PlayerStore updateRatios", this.layerStyle)
        this.layerStyle = {
          width: `${width + 1}px`,
          height: `${height + 1}px`,
          left: `${left}px`,
          top: `${top}px`,
        }
        this.layerDimensions = {
          top: top,
          left: left,
          height: height,
          width: width,
        }
      } 

    },
    enterFullscreen() {
      log.info(1,"🌜 PlayerStore enterFullscreen")
      // this.attachVideoElement()
      // this.videoElement?.requestFullscreen()
      this.player.requestFullscreen()
    },
    exitFullscreen() {
      log.info(1,"🌜 PlayerStore exitFullscreen")
      this.player.exitFullscreen()
    },

    // Method to initialize text tracks based on the new video
    initializeTextTracks(captionQuery=null) {
      const playbackStore = PlaybackStore();
      let forcedCaptions = captionQuery || playbackStore.captions
      // log.info(1,"🌜 PlayerStore initializeTextTracks", forcedCaptions)
      const videoStore = VideoStore();
      const video = videoStore.video;
      if (!this.player) return;

      // Store the currently active text track before clearing
      const activeTrack = this.playerState?.textTrack;
      const activeTrackLabel = activeTrack?.label;
      const activeTrackLanguage = activeTrack?.language;
      log.info(1,"🌜 PlayerStore storing active track before clear:", activeTrackLabel, activeTrackLanguage);

      // Clear existing tracks
      while (this.player.textTracks.length > 0) {
        this.player.textTracks.remove(this.player.textTracks[0]);
      }
      log.info(1,"🌜 PlayerStore new TextTracks", this.player.textTracks)

      this.player.textTracks.clear();
      log.info(1,"🌜 PlayerStore new TextTracks after clear", this.player.textTracks)

      // Initialize new tracks
      const newTracks = [];


      // Add chapters if available
      if (video.chapters && video.chapters.length > 0) {
        newTracks.push({
          src: `/backend/videos/${video.id}/chapters`,
          kind: "chapters",
          language: "en-US",
          default: true,
        });
      }

      // Add captions
      // Add subtitles
      // log.info(1,"🌜 Player initializeTextTracks", video.subtitles);
      if (video.subtitles && video.subtitles.length > 0) {
        video.subtitles.forEach((subtitle) => {
          log.info(1,
            "🌜 Player initializeTextTracks subtitle",
            subtitle.language,
            subtitle.label,
            subtitle.url
          );
          let isDefaultCaptions = false;
          // Preserve active track selection or use defaults
          if (activeTrackLabel && activeTrackLabel === subtitle.label) {
            isDefaultCaptions = true;
            log.info(1,"🌜 PlayerStore preserving active track:", subtitle.label);
          } else if (
            forcedCaptions &&
            forcedCaptions.toLowerCase() === subtitle.label.toLowerCase()
          ) {
            isDefaultCaptions = true;
          } else if (!forcedCaptions && video.default_captions === subtitle.label) {
            // Use the video's default captions if no active track exists
            isDefaultCaptions = true;
            log.info(1,"🌜 PlayerStore using video's default captions:", subtitle.label);
          }
          newTracks.push({
            src: subtitle.url,
            kind: "subtitles",
            label: subtitle.label,
            language: subtitle.language,
            default: isDefaultCaptions,
            crossorigin: "anonymous",
            type: subtitle.url.includes("srt") ? "srt" : "vtt",
          });
        });
      }


      // Add new tracks to the player
      newTracks.forEach((track) => {
        this.player.textTracks.add(track);
      });

      // Update the player store
      this.activeTracks = newTracks;

      // Manually activate the appropriate track
      setTimeout(() => {
        const tracks = this.player.textTracks.toArray();
        let trackToActivate = null;
        
        // First try to find the previously active track
        if (activeTrackLabel) {
          trackToActivate = tracks.find(t => t.label === activeTrackLabel && t.kind === 'subtitles');
          if (trackToActivate) {
            log.info(1,"🌜 PlayerStore activating preserved track:", activeTrackLabel);
          }
        }
        
        // If no active track was preserved, look for default track for this video
        if (!trackToActivate && video.default_captions) {
          trackToActivate = tracks.find(t => t.label === video.default_captions && t.kind === 'subtitles');
          if (trackToActivate) {
            log.info(1,"🌜 PlayerStore activating video's default track:", video.default_captions);
          }
        }
        
        // Activate the selected track
        if (trackToActivate) {
          trackToActivate.mode = 'showing';
          log.info(1,"🌜 PlayerStore track activated, mode set to:", trackToActivate.mode);
        } else if (activeTrackLabel || video.default_captions) {
          log.info(1,"🌜 PlayerStore could not find track to activate. Looked for:", activeTrackLabel || video.default_captions);
        }
      }, 100);

      // log.info(1,"📝 Text tracks initialized:", newTracks);
    }
  },
  getters: {
    isMuted() {
      return this.videoElement?.muted || this.playerState.muted
    },
    getVolume() {
      return this.playerState.volume
    },
    isWaiting() {
      return this.playerState.waiting
    },
    safeVideoElement() {
      return this.videoElement || this.player
    },
    safeDuration() {
      // Re-attach video element if it's null or stale
      if (!this.videoElement || (this.videoElement && !document.body.contains(this.videoElement))) {
        this.attachVideoElement();
      }
      return this.videoElement?.duration || this.playerState.duration
    }
  }
})